import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BackButton,
  Button,
  Card,
  EmptyState,
  LoadingState,
  MobileSearchField,
  SectionHeader,
  Select,
  StatusBadge,
  Typography,
} from '../../components/common';
import {
  DivisionTable,
  FixtureRow,
  LeagueSeasonSelect,
  MatchCardModal,
} from '../../components/leagues/LeagueMatchViews';
import {
  fetchMatchCard,
  fetchMiddlesexSeason,
  fetchMiddlesexSeasons,
} from '../../services/leagueService';
import { sortLeagueFixturesUpcomingFirst } from '../../utils/leagueTeamSelection';

const COMPETITION_ORDER = ['open', 'ladies', 'mixed', 'hybrid'];
const FIXTURE_PREVIEW_COUNT = 5;
const FILTER_LABEL_CLASS = 'mb-1 block text-xs font-medium text-text-muted';

const MiddlesexLeaguePage = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [startYear, setStartYear] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [competitionId, setCompetitionId] = useState(null);
  const [divisionId, setDivisionId] = useState(null);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [seasonsReady, setSeasonsReady] = useState(false);
  const [showAllFixtures, setShowAllFixtures] = useState(false);
  const [clubQuery, setClubQuery] = useState('');
  const competitionCodeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // location.key is 'default' when the page was opened directly, so there is no in-app page to return to.
  const handleBack = useCallback(() => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/');
  }, [location.key, navigate]);

  useEffect(() => {
    let cancelled = false;
    fetchMiddlesexSeasons()
      .then((rows) => {
        if (cancelled) return;
        setSeasons(rows);
        setStartYear((prev) => (rows.some((row) => row.start_year === prev) ? prev : rows[0]?.start_year ?? null));
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message);
      })
      .finally(() => {
        if (!cancelled) setSeasonsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (startYear == null) return undefined;
    let cancelled = false;
    fetchMiddlesexSeason(startYear)
      .then((data) => {
        if (cancelled) return;
        setSnapshot(data);
        const list = [...(data?.competitions ?? [])].sort(
          (a, b) => COMPETITION_ORDER.indexOf(a.code) - COMPETITION_ORDER.indexOf(b.code),
        );
        const prevCode = competitionCodeRef.current;
        setCompetitionId(list.find((item) => item.code === prevCode)?.id ?? list[0]?.id ?? null);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [startYear]);

  const teamsById = useMemo(
    () => new Map((snapshot?.teams ?? []).map((team) => [team.id, team])),
    [snapshot],
  );

  const competitions = useMemo(
    () =>
      [...(snapshot?.competitions ?? [])].sort(
        (a, b) => COMPETITION_ORDER.indexOf(a.code) - COMPETITION_ORDER.indexOf(b.code),
      ),
    [snapshot],
  );

  const divisions = useMemo(
    () => (snapshot?.divisions ?? []).filter((row) => row.competition_id === competitionId),
    [snapshot, competitionId],
  );

  const competitionOptions = useMemo(
    () => competitions.map((item) => ({ value: item.id, label: item.label })),
    [competitions],
  );

  const divisionOptions = useMemo(
    () => divisions.map((item) => ({ value: item.id, label: item.name })),
    [divisions],
  );

  useEffect(() => {
    if (!divisions.length) {
      setDivisionId(null);
      return;
    }
    if (!divisions.some((row) => row.id === divisionId)) {
      setDivisionId(divisions[0].id);
    }
  }, [divisions, divisionId]);

  const divisionStandings = useMemo(
    () => (snapshot?.standings ?? []).filter((row) => row.division_id === divisionId),
    [snapshot, divisionId],
  );

  const fixtures = useMemo(
    () => sortLeagueFixturesUpcomingFirst(
      (snapshot?.fixtures ?? []).filter((fixture) => fixture.division_id === divisionId),
    ),
    [snapshot, divisionId],
  );

  const division = divisions.find((row) => row.id === divisionId) ?? null;
  const competition = competitions.find((row) => row.id === competitionId) ?? null;
  competitionCodeRef.current = competition?.code ?? null;

  const filteredFixtures = useMemo(() => {
    const query = clubQuery.trim().toLowerCase();
    if (!query) return fixtures;
    return fixtures.filter((fixture) =>
      [fixture.home_team_id, fixture.away_team_id].some((id) =>
        (teamsById.get(id)?.name ?? '').toLowerCase().includes(query),
      ),
    );
  }, [fixtures, clubQuery, teamsById]);

  useEffect(() => {
    setClubQuery('');
  }, [divisionId, startYear]);
  useEffect(() => {
    setShowAllFixtures(false);
  }, [divisionId, startYear, clubQuery]);
  const visibleFixtures = showAllFixtures ? filteredFixtures : filteredFixtures.slice(0, FIXTURE_PREVIEW_COUNT);

  const openFixture = useCallback(async (fixture) => {
    setSelectedFixture(fixture);
    try {
      const card = await fetchMatchCard(fixture.id);
      setSelectedCard(card);
    } catch {
      setSelectedCard({ lineups: [], rubbers: [], players: [] });
    }
  }, []);

  if (loadError) {
    return <EmptyState title="Could not load league data" description={loadError} />;
  }

  if (!snapshot) {
    if (seasonsReady && seasons.length === 0) {
      return (
        <EmptyState
          title="No Middlesex data yet"
          description="Run the scraper against local Docker: node scripts/league-scraper/scrapeMcba.mjs --season=2025 --write-db"
        />
      );
    }
    return <LoadingState message="Loading Middlesex league…" className="min-h-[50vh]" />;
  }

  if (!snapshot.competitions.length) {
    return (
      <EmptyState
        title="No Middlesex data yet"
        description="Run the scraper against local Docker: node scripts/league-scraper/scrapeMcba.mjs --season=2025 --write-db"
      />
    );
  }

  return (
    <div className="space-y-6 pb-16">
      <header>
        <div className="mb-3">
          <BackButton onClick={handleBack}>Back</BackButton>
        </div>
        <StatusBadge variant="info" size="small">Middlesex · mirrored public results</StatusBadge>
        <Typography variant="h2" as="h1" className="mt-3 text-2xl sm:text-3xl">
          Middlesex County League
        </Typography>
        <Typography variant="muted" className="mt-1">
          {snapshot.fixtures.length} fixtures
        </Typography>
        <div className="mt-4 grid gap-3 sm:max-w-2xl sm:grid-cols-3">
          <div>
            <span className={FILTER_LABEL_CLASS} aria-hidden="true">Season</span>
            <LeagueSeasonSelect
              id="middlesex-season-select"
              seasons={seasons}
              value={startYear}
              onChange={setStartYear}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="middlesex-competition-select" className={FILTER_LABEL_CLASS}>League</label>
            <Select
              id="middlesex-competition-select"
              options={competitionOptions}
              value={competitionId}
              onChange={(event) => setCompetitionId(event.target.value)}
              placeholder=""
            />
          </div>
          <div>
            <label htmlFor="middlesex-division-select" className={FILTER_LABEL_CLASS}>Division</label>
            <Select
              id="middlesex-division-select"
              options={divisionOptions}
              value={divisionId}
              onChange={(event) => setDivisionId(event.target.value)}
              placeholder=""
              disabled={!divisionOptions.length}
            />
          </div>
        </div>
      </header>

      <section>
        <SectionHeader title={division ? `${competition?.label} · ${division.name}` : 'Table'} size="md" />
        <Card size="small" className="mt-3 overflow-x-auto p-0 sm:p-0">
          <DivisionTable
            standings={divisionStandings}
            teamsById={teamsById}
            divisionName={division?.name}
          />
        </Card>
      </section>
      <section>
        <SectionHeader title="Fixtures" subtitle="Newest first" size="md" />
        {fixtures.length > 0 ? (
          <div className="mt-3 max-w-md">
            <MobileSearchField
              value={clubQuery}
              onChange={(event) => setClubQuery(event.target.value)}
              placeholder="Search clubs…"
              aria-label="Search fixtures by club"
              showIcon
            />
          </div>
        ) : null}
        <div className="mt-3 space-y-2">
          {fixtures.length === 0 ? (
            <EmptyState title="No fixtures" description="Nothing in this division yet." />
          ) : filteredFixtures.length === 0 ? (
            <EmptyState title="No matching fixtures" description={`No club in this division matches “${clubQuery.trim()}”.`} />
          ) : (
            <>
              {visibleFixtures.map((fixture) => (
                <FixtureRow
                  key={fixture.id}
                  fixture={fixture}
                  teamsById={teamsById}
                  onOpen={() => openFixture(fixture)}
                />
              ))}
              {filteredFixtures.length > FIXTURE_PREVIEW_COUNT ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  className="w-full"
                  aria-expanded={showAllFixtures}
                  onClick={() => setShowAllFixtures((prev) => !prev)}
                >
                  {showAllFixtures ? 'Show fewer' : `Show all ${filteredFixtures.length} fixtures`}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </section>

      {selectedFixture && (
        <MatchCardModal
          fixture={selectedFixture}
          card={selectedCard}
          teamsById={teamsById}
          competitionLabel={competitions.find((row) => row.id === selectedFixture.competition_id)?.label}
          divisionName={snapshot.divisions.find((row) => row.id === selectedFixture.division_id)?.name}
          onClose={() => {
            setSelectedFixture(null);
            setSelectedCard(null);
          }}
        />
      )}
    </div>
  );
};

export default MiddlesexLeaguePage;
